import { useState } from 'react';
import {
  Table2,
  ChevronRight,
  ChevronDown,
  Key,
  Hash,
  Type,
  Calendar,
  ToggleLeft,
  FileText,
  Search,
  RefreshCw,
  Loader2,
  Database,
} from 'lucide-react';

interface ColumnSchema {
  columnName: string;
  dataType: string;
  columnType: string;
  nullable: boolean;
  columnKey: string;
  defaultValue: string | null;
  extra: string;
}

interface TableSchema {
  tableName: string;
  tableType: string;
  rowCount: number;
  columns: ColumnSchema[];
}

interface TableBrowserProps {
  tables: TableSchema[];
  isLoading: boolean;
  onRefresh: () => void;
  onSelectTable: (tableName: string) => void;
  onInsertTableName: (tableName: string) => void;
}

export function TableBrowser({
  tables,
  isLoading,
  onRefresh,
  onSelectTable,
  onInsertTableName,
}: TableBrowserProps) {
  const [search, setSearch] = useState('');
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());

  // Filter tables by search
  const filteredTables = tables.filter((table) =>
    table.tableName.toLowerCase().includes(search.toLowerCase())
  );

  // Toggle table expansion
  const toggleTable = (tableName: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      if (next.has(tableName)) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
  };

  // Get icon for column type
  const getColumnIcon = (dataType: string, columnKey: string) => {
    if (columnKey === 'PRI') return <Key className="h-3 w-3 text-amber-400" />;
    if (columnKey === 'UNI') return <Key className="h-3 w-3 text-purple-400" />;
    if (columnKey === 'MUL') return <Key className="h-3 w-3 text-slate-500" />;

    const type = dataType.toLowerCase();
    if (type.includes('int') || type.includes('decimal') || type.includes('float') || type.includes('double')) {
      return <Hash className="h-3 w-3 text-cyan-400" />;
    }
    if (type.includes('varchar') || type.includes('text') || type.includes('char')) {
      return <Type className="h-3 w-3 text-green-400" />;
    }
    if (type.includes('date') || type.includes('time')) {
      return <Calendar className="h-3 w-3 text-blue-400" />;
    }
    if (type.includes('bool') || type.includes('tinyint')) {
      return <ToggleLeft className="h-3 w-3 text-pink-400" />;
    }
    if (type.includes('blob') || type.includes('binary')) {
      return <FileText className="h-3 w-3 text-slate-500" />;
    }
    return <Type className="h-3 w-3 text-slate-500" />;
  };

  // Format row count
  const formatRowCount = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  return (
    <div className="flex flex-col h-full bg-slate-900/50 border border-slate-800 rounded-lg">
      {/* Database Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800 bg-slate-800/30">
        <Database className="h-4 w-4 text-cyan-400" />
        <span className="text-sm font-medium text-cyan-400">playtark</span>
      </div>

      {/* Tables Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Tables</span>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-300 disabled:opacity-50"
          title="Refresh tables"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Search */}
      <div className="px-2 py-2 border-b border-slate-800">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter tables..."
            className="w-full pl-7 pr-3 py-1.5 text-xs bg-slate-800 border border-slate-700 rounded text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </div>
      </div>

      {/* Table list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          </div>
        ) : filteredTables.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-500">
            {search ? 'No tables match your search' : 'No tables found'}
          </div>
        ) : (
          <div className="py-1">
            {filteredTables.map((table) => {
              const isExpanded = expandedTables.has(table.tableName);

              return (
                <div key={table.tableName}>
                  {/* Table row */}
                  <div
                    className="flex items-center gap-1 px-2 py-1.5 hover:bg-slate-800/50 cursor-pointer group"
                    onClick={() => toggleTable(table.tableName)}
                  >
                    <button className="p-0.5">
                      {isExpanded ? (
                        <ChevronDown className="h-3 w-3 text-slate-500" />
                      ) : (
                        <ChevronRight className="h-3 w-3 text-slate-500" />
                      )}
                    </button>
                    <Table2 className="h-3.5 w-3.5 text-cyan-400" />
                    <span
                      className="flex-1 text-xs text-slate-300 truncate"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        onInsertTableName(table.tableName);
                      }}
                      title={`Double-click to insert "${table.tableName}"`}
                    >
                      {table.tableName}
                    </span>
                    <span className="text-xs text-slate-600 opacity-0 group-hover:opacity-100">
                      {formatRowCount(table.rowCount)}
                    </span>
                  </div>

                  {/* Columns (expanded) */}
                  {isExpanded && (
                    <div className="ml-5 border-l border-slate-800">
                      {table.columns.map((col) => (
                        <div
                          key={col.columnName}
                          className="flex items-center gap-2 pl-3 pr-2 py-1 hover:bg-slate-800/30 cursor-pointer"
                          onClick={() => onInsertTableName(col.columnName)}
                          title={`${col.columnType}${col.nullable ? '' : ' NOT NULL'}${col.extra ? ` (${col.extra})` : ''}`}
                        >
                          {getColumnIcon(col.dataType, col.columnKey)}
                          <span className="flex-1 text-xs text-slate-400 truncate">
                            {col.columnName}
                          </span>
                          <span className="text-xs text-slate-600 truncate max-w-[80px]">
                            {col.dataType}
                          </span>
                        </div>
                      ))}

                      {/* Quick actions */}
                      <div className="flex items-center gap-1 pl-3 pr-2 py-1.5 border-t border-slate-800/50">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectTable(table.tableName);
                          }}
                          className="text-xs text-cyan-400 hover:text-cyan-300"
                        >
                          SELECT *
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-slate-800 text-xs text-slate-500">
        {tables.length} table{tables.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
